import BodyColumnData, {
  BodyColumnValue,
} from "../components/NotificationsScreen/BodyColumnData.model";

import HeaderColumnData, {
  SubColumn,
} from "../services/models/notificationsSettingsTableModels/headerColumnData.model";

export interface ApiMediumValue {
  id: string;
  key: string;
  name: string;
  defaultValue: boolean;
  isEditable: boolean;
  createdAt: null | Date;
  updatedAt: null | Date;
  disabledHoverText: string;
  isReportDelivery: boolean;
}

export interface ApiOrganizationMediumSettingsValue {
  id: number;
  notificationMediumId: number;
  organizationId: number;
  value: boolean;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface ApiUserNotificationMediumValue {
  id: number;
  notificationMediumId: number;
  organizationId: number;
  value: boolean;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface ApiUserNotificationTypeValue {
  id: number;
  notificationTypeId: number;
  organizationId: number;
  userId: number;
  value: boolean;
  createdAt: Date | null;
  updatedAt: Date | null;
}

export interface ApiNotificationTypeValue {
  id: number | string;
  key: string;
  name: string;
  defaultValue: boolean;
  isOfficeManager: boolean;
  isPhysician: boolean;
  isCOA: boolean;
  isPLU: boolean;
  createdAt: Date | null;
  updatedAt: Date | null;
}

class NotificationSettingsServices {
  private mediumValues: ApiMediumValue[] = [];
  private organizationMediumValues: ApiOrganizationMediumSettingsValue[] = [];
  private userMediumValues: ApiUserNotificationMediumValue[] = [];
  private notificationTypes: ApiNotificationTypeValue[] = [];
  private userNotificationTypesValues: ApiUserNotificationTypeValue[] = [];
  private tableBody: Array<BodyColumnData[]> = [];
  private tableColumn: HeaderColumnData[] = [];
  private typeTableColumn: HeaderColumnData[] = [];
  private personalNotificationMediumTableData: Array<BodyColumnData[]> = [];
  private personalNotificationTypeTableData: Array<BodyColumnData[]> = [];
  private notificationMediumsSubColumns: SubColumn[] = [];
  private reportDeliverySubColumn: SubColumn[] = [];
  private notificationTypeSubColumns: SubColumn[] = [];
  private organizations: any = [];
  private isPersonalSettingsMedium: boolean;

  constructor(
    organizations: any,
    mediumValues: ApiMediumValue[],
    organizationMediumValues: ApiOrganizationMediumSettingsValue[],
    userMediumValues?: ApiUserNotificationMediumValue[],
    notificationTypes?: ApiNotificationTypeValue[],
    userNotificationTypesValues?: ApiUserNotificationTypeValue[],
    isPersonalSettingsMedium: boolean = false
  ) {
    this.organizations = organizations;
    this.mediumValues = mediumValues;
    this.organizationMediumValues = organizationMediumValues;
    this.userMediumValues = userMediumValues ?? [];
    this.notificationTypes = notificationTypes ?? [];
    this.userNotificationTypesValues = userNotificationTypesValues ?? [];
    this.isPersonalSettingsMedium = isPersonalSettingsMedium;
  }
  private buildUiKey(prefix: string, id: string | number, key: string) {
    return `${prefix}${String(id)}_${key}`;
  }
  private createNotificationMediums(isPersonalSetting: boolean) {
    this.notificationMediumsSubColumns = [];
    this.reportDeliverySubColumn = [];

    // Map all notification mediums in the table header
    this.mediumValues.forEach((mediumValue) => {
      let settingExist = this?.organizationMediumValues?.some?.(
        (orgMediumValue) =>
          orgMediumValue.notificationMediumId == Number(mediumValue.id)
      );

      const mediumUiKey = this.buildUiKey("medium_", mediumValue.id, mediumValue.key);
      const reportUiKey = this.buildUiKey("report_", mediumValue.id, mediumValue.key);

      let mediumData: SubColumn = {
        key: mediumValue.key,
        uniqueKey: mediumUiKey,
        id: mediumValue.id,
        name: mediumValue.name,
        allSelected: false,
        disabled:
          this.organizationMediumValues.length == 0 && isPersonalSetting
            ? true
            : settingExist == false && isPersonalSetting
            ? true
            : mediumValue.isEditable
            ? false
            : true,
      };

      let mediumReportDeliveryData: SubColumn = {
        key: mediumValue.key,
        uniqueKey: reportUiKey,
        id: mediumValue.id,
        name: mediumValue.name,
        allSelected: false,
        disabled:
          this.organizationMediumValues.length == 0 && isPersonalSetting
            ? true
            : settingExist == false && isPersonalSetting
            ? true
            : mediumValue.isEditable
            ? false
            : true,
      };

      if (!mediumValue.isReportDelivery) {
        this.notificationMediumsSubColumns.push(mediumData);
      }

      if (mediumValue.isReportDelivery) {
        this.reportDeliverySubColumn.push(mediumReportDeliveryData);
      }
    });

    this.tableColumn.push(
      {
        columnName: "Notification Medium",
        subColumns: this.notificationMediumsSubColumns,
      },
      {
        columnName: "Report Delivery",
        subColumns: this.reportDeliverySubColumn,
      }
    );
  }

  private createNotificationType() {
    this.notificationTypeSubColumns = [];
    // Map all notification type in the table header
    this.notificationTypes.forEach((notificationValue) => {
      const typeUiKey = this.buildUiKey(
        "type_",
        notificationValue.id,
        notificationValue.key
      );
      let notificationData: SubColumn = {
        key: notificationValue.key,
        uniqueKey: typeUiKey,
        id: notificationValue.id,
        name: notificationValue.name,
        allSelected: false,
        disabled: false,
      };

      this.notificationTypeSubColumns.push(notificationData);
    });

    this.typeTableColumn.push({
      columnName: "Notification Types",
      subColumns: this.notificationTypeSubColumns,
    });
  }

  private createTableData(isPersonalSetting: boolean) {
    // Reset data to avoid duplicates
    this.personalNotificationMediumTableData = [];
    this.tableBody = [];
  
    if (this.isPersonalSettingsMedium) {
      // Logic for personal settings (no organizations)
      let rowData = [];
      let notificationsMediums: BodyColumnValue[] = [];
      let reportDelivery: BodyColumnValue[] = [];
  
      this.mediumValues.forEach((mediumValue) => {
        const mediumUiKey = this.buildUiKey(
          "medium_",
          mediumValue.id,
          mediumValue.key
        );
        const reportUiKey = this.buildUiKey(
          "report_",
          mediumValue.id,
          mediumValue.key
        );
        let mediumDataValue = mediumValue.defaultValue;
        let mediumDisabled = false;
        let hoverText = mediumValue.disabledHoverText;
  
        // Find the user's personal setting for the medium
        let personalSettingForTheMedium = this?.userMediumValues?.find?.(
          (userMedium) =>
            userMedium.notificationMediumId == Number(mediumValue.id)
        );
  
        if (isPersonalSetting) {
          mediumDataValue =
            personalSettingForTheMedium?.value !== undefined
              ? personalSettingForTheMedium.value
              : mediumValue.defaultValue;
  
          mediumDisabled = mediumValue.isEditable == true ? false : true;
        }
  
        const baseMediumData: BodyColumnValue = {
          key: mediumValue.key,
          uiKey: mediumUiKey,
          id: mediumValue.id,
          name: mediumValue.name,
          value: mediumDataValue ? mediumDataValue : false,
          type: "switch",
          disabled: mediumDisabled,
          hoverText: mediumDisabled ? hoverText : null,
        };
  
        if (!mediumValue.isReportDelivery) {
          notificationsMediums.push(baseMediumData);
        }
  
        if (mediumValue.isReportDelivery) {
          reportDelivery.push({
            ...baseMediumData,
            uiKey: reportUiKey,
          });
        }
      });
  
      // Add the data to the row
      rowData.push({ data: notificationsMediums });
      rowData.push({ data: reportDelivery });
  
      // Add the row to the table body
      if (isPersonalSetting) {
        this.personalNotificationMediumTableData.push(rowData);
      } else {
        this.tableBody.push(rowData);
      }
    } else {
      // Existing logic for organization settings
      this.organizations.forEach((organization: any) => {
        let rowData = [];
        let notificationsMediums: BodyColumnValue[] = [];
        let reportDelivery: BodyColumnValue[] = [];
        let organizationColumnData: BodyColumnValue = {
          disabled: false,
          hoverText: null,
          key: "Organization",
          name: organization.name,
          id: organization.id,
          value: organization.id,
          type: "text",
        };
  
        this.mediumValues.forEach((mediumValue) => {
          const mediumUiKey = this.buildUiKey(
            "medium_",
            mediumValue.id,
            mediumValue.key
          );
          const reportUiKey = this.buildUiKey(
            "report_",
            mediumValue.id,
            mediumValue.key
          );
          let mediumDataValue = mediumValue.defaultValue;
          let mediumDisabled = false;
          let hoverText = mediumValue.disabledHoverText;
          let organizationValueForTheMedium =
            this?.organizationMediumValues?.find?.(
              (orgMediumValue) =>
                orgMediumValue.notificationMediumId == Number(mediumValue.id) &&
                orgMediumValue.organizationId == organization.id
            )?.value;
  
          let personalSettingForTheMedium = this?.userMediumValues?.find?.(
            (userMedium) =>
              userMedium.organizationId === organization.id &&
              userMedium.notificationMediumId == Number(mediumValue.id)
          );
  
          if (isPersonalSetting) {
            mediumDataValue =
              organizationValueForTheMedium === true
                ? personalSettingForTheMedium?.value !== undefined
                  ? personalSettingForTheMedium.value
                  : organizationValueForTheMedium
                : mediumValue.defaultValue;
  
            mediumDisabled =
              organizationValueForTheMedium === undefined
                ? true
                : organizationValueForTheMedium === false
                ? true
                : mediumValue.isEditable == true
                ? false
                : true;
          } else {
            mediumDataValue = organizationValueForTheMedium
              ? organizationValueForTheMedium
              : mediumValue.defaultValue;
            mediumDisabled = mediumValue.isEditable == true ? false : true;
          }
  
          const baseMediumData: BodyColumnValue = {
            key: mediumValue.key,
            uiKey: mediumUiKey,
            id: mediumValue.id,
            name: mediumValue.name,
            value: mediumDataValue ? mediumDataValue : false,
            type: "switch",
            disabled: mediumDisabled,
            hoverText: mediumDisabled ? hoverText : null,
          };
          if (!mediumValue.isReportDelivery) {
            notificationsMediums.push(baseMediumData);
          }
  
          if (mediumValue.isReportDelivery) {
            reportDelivery.push({
              ...baseMediumData,
              uiKey: reportUiKey,
            });
          }
        });
        rowData.push({ data: { ...organizationColumnData } });
        rowData.push({ data: notificationsMediums });
        rowData.push({ data: reportDelivery });
  
        if (isPersonalSetting) {
          this.personalNotificationMediumTableData.push(rowData);
        } else {
          this.tableBody.push(rowData);
        }
      });
    }
  }
  private createNotificationTypeTableData() {
    this.personalNotificationTypeTableData = [];
    // Create Body data for table with respective notification medium values
    this.organizations.forEach((organization: any) => {
      let rowData = [];
      let notificationTypes: BodyColumnValue[] = [];
      let organizationColumnData: BodyColumnValue = {
        disabled: false,
        key: "Organization",
        name: organization.name,
        id: organization.id,
        value: organization.id,
        type: "text",
        hoverText: null,
      };

      this.notificationTypes.forEach((notificationValue) => {
        const typeUiKey = this.buildUiKey(
          "type_",
          notificationValue.id,
          notificationValue.key
        );
        let organizationValueForTheType =
          this?.userNotificationTypesValues?.find?.(
            (userTypeValue) =>
              userTypeValue.notificationTypeId == notificationValue.id &&
              userTypeValue.organizationId == organization.id
          )?.value;

        let organizationTypeData: BodyColumnValue = {
          key: notificationValue.key,
          uiKey: typeUiKey,
          id: notificationValue.id,
          name: notificationValue.name,
          value:
            organizationValueForTheType !== undefined
              ? organizationValueForTheType
              : notificationValue.defaultValue,
          type: "switch",
          disabled: false,
          hoverText: null,
        };

        notificationTypes.push(organizationTypeData);
      });

      rowData.push({ data: { ...organizationColumnData } });
      rowData.push({ data: notificationTypes });

      this.personalNotificationTypeTableData.push(rowData);
    });
  }

  handleSelectAll(
    id: string | number,
    tableColumn: HeaderColumnData[],
    tableBody: Array<BodyColumnData[]>,
    dispatch: any,
    value?: boolean
  ) {
    const matchesSubColumn = (subColumn: SubColumn) => {
      if (typeof id === "string") {
        if (subColumn.uniqueKey && subColumn.uniqueKey === id) {
          return true;
        }
        return String(subColumn.id) === id;
      }
      return subColumn.id === id;
    };

    const matchesBodyValue = (bodyValue: BodyColumnValue) => {
      if (typeof id === "string") {
        if (bodyValue.uiKey && bodyValue.uiKey === id) {
          return true;
        }
        return String(bodyValue.id) === id;
      }
      return bodyValue.id === id;
    };

    let disabledCount: number = 0;
    let tempTableColumn: HeaderColumnData[] = JSON.parse(
      JSON.stringify(tableColumn)
    );
    let tempTableBody: Array<BodyColumnData[]> = JSON.parse(
      JSON.stringify(tableBody)
    );
    let allSelectedValue = false;

    tempTableColumn = tempTableColumn.map((column) => ({
      ...column,
      subColumns: column.subColumns.map((subColumn) => {
        const isTarget = matchesSubColumn(subColumn);
        if (isTarget) {
          allSelectedValue =
            value !== undefined ? value : !subColumn.allSelected;
        }
        return {
          ...subColumn,
          allSelected:
            isTarget
              ? value !== undefined
                ? value
                : !subColumn.allSelected
              : subColumn.allSelected,
        };
      }),
    }));

    tempTableBody = tempTableBody.map((value) =>
      value.map((item) => {
        if (Array.isArray(item.data)) {
          disabledCount =
            disabledCount +
            item.data.filter(
              (value) => matchesBodyValue(value) && value.disabled
            ).length;
          return {
            ...item,
            data: item.data.map((value) => ({
              ...value,
              value:
                matchesBodyValue(value) && value.disabled === false
                  ? allSelectedValue
                  : value.value,
            })),
          };
        }
        return item;
      })
    );
    if (disabledCount > 0) {
      // dispatch(NotificationActions.showNotification({
      //   // type: 'suc',
      //   type: 'success',
      //   title: 'Notifications Status',
      //   hideClose: false,
      //   message: `${tempTableBody?.length - disabledCount} of ${tempTableBody?.length} notifications have been ${allSelectedValue === false ? 'disabled':'enabled'} but ${disabledCount} notification was restricted by organization.`,
      //   customFunction() {},
      //   buttons: [
      //     {
      //       onClick: () => {
      //         dispatch(NotificationActions.hideNotifications());
      //       },
      //       title: 'OK',
      //       type: 'primary',
      //     },
      //   ],
      // }));
    }

    return {
      tableColumn: tempTableColumn,
      tableBody: tempTableBody,
    };
  }

  createOrganizationsSettingsData() {
    this.tableColumn = [
      {
        columnName: "Organizations",
        subColumns: [],
      },
    ];
    this.createNotificationMediums(false);
    this.createTableData(false);
  }

  createPersonalOrganizationsSettingsData() {
    this.tableColumn = [];

    this.createNotificationMediums(true);
    this.createTableData(true);
  }

  createPersonalNotificationTypeData() {
    this.typeTableColumn = [
      {
        columnName: "Organizations",
        subColumns: [],
      },
    ];

    this.createNotificationType();
    this.createNotificationTypeTableData();
  }

  getOrganizationsSettingsData(): {
    tableBody: Array<BodyColumnData[]>;
    tableColumn: HeaderColumnData[];
  } {
    return {
      tableBody: this.tableBody,
      tableColumn: this.tableColumn,
    };
  }
  getPersonalOrganizationsSettingsData(): {
    tableBody: Array<BodyColumnData[]>;
    tableColumn: HeaderColumnData[];
  } {
    return {
      tableBody: this.personalNotificationMediumTableData,
      tableColumn: this.tableColumn,
    };
  }

  getPersonalNotificationTypeData(): {
    tableBody: Array<BodyColumnData[]>;
    tableColumn: HeaderColumnData[];
  } {
    return {
      tableBody: this.personalNotificationTypeTableData,
      tableColumn: this.typeTableColumn,
    };
  }
}

export default NotificationSettingsServices;
